import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHero, StatHero, ActionTile, StatusPill } from "@/components/shared/Visual";
import { ShieldCheck, Building2, Calendar, Briefcase, Users2, AlertCircle, MousePointerClick, TrendingUp, Activity } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

export default function AdminDashboard() {
  const [s, setS] = useState({});
  const [cov, setCov] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard-stats").then((r) => setS(r.data || {})).catch(() => {});
    api.get("/admin/cover-analytics").then((r) => setCov(r.data || null)).catch(() => {});
  }, []);

  const pending = (s.pending_staff || 0) + (s.pending_companies || 0);

  return (
    <div className="space-y-6" data-testid={ADMIN.dashboard}>
      <PageHero
        eyebrow="Command Center"
        title="Live Overview"
        sub="Approvals · Bookings · Network"
        right={<div className="flex gap-2"><StatusPill kind="live">Systems Live</StatusPill></div>}
      />

      {pending > 0 && (
        <div className="card-dark p-4 border-l-4 border-l-amber-400 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400" />
          <div className="flex-1">
            <div className="font-outfit font-semibold txt-hi text-sm">{pending} pending review</div>
            <div className="eyebrow mt-1">{s.pending_staff || 0} staff · {s.pending_companies || 0} companies</div>
          </div>
          <Link to="/admin/staff" className="btn-ghost-dark text-xs">Review</Link>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatHero label="Pending Staff" value={s.pending_staff || 0} icon={ShieldCheck} tone="amber" testId="admin-stat-pending-staff" to="/admin/staff" />
        <StatHero label="Pending Companies" value={s.pending_companies || 0} icon={Building2} tone="amber" testId="admin-stat-pending-co" to="/admin/companies" />
        <StatHero label="Active Bookings" value={s.active_bookings || 0} icon={Calendar} tone="gold" testId="admin-stat-bookings" to="/admin/bookings" />
        <StatHero label="Requests" value={s.booking_requests || 0} icon={Calendar} tone="cyan" testId="admin-stat-requests" to="/admin/bookings" />
        <StatHero label="Verified Staff" value={s.total_staff || 0} icon={ShieldCheck} tone="brand" testId="admin-stat-staff" to="/admin/staff" />
        <StatHero label="Verified Companies" value={s.total_companies || 0} icon={Building2} tone="brand" testId="admin-stat-companies" to="/admin/companies" />
        <StatHero label="Open Jobs" value={s.active_jobs || 0} icon={Briefcase} tone="violet" testId="admin-stat-jobs" to="/admin/jobs" />
        <StatHero label="Cluster Teams" value={s.teams || 0} icon={Users2} tone="cyan" testId="admin-stat-teams" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <ActionTile to="/admin/control-room" icon={Activity} label="Control Room" sub="Live ops" tone="cyan" testId="admin-tile-control-room" />
        <ActionTile to="/admin/staff" icon={ShieldCheck} label="Staff" sub="Approve · verify" testId={ADMIN.pendingStaff} />
        <ActionTile to="/admin/companies" icon={Building2} label="Companies" sub="Approve · verify" tone="violet" testId={ADMIN.pendingCompanies} />
        <ActionTile to="/admin/bookings" icon={Calendar} label="Bookings" sub="All ops" tone="emerald" />
      </div>

      {cov && (
        <div className="card-dark p-6" data-testid="admin-cover-analytics">
          <div className="flex items-center gap-2 mb-4">
            <MousePointerClick className="w-4 h-4 text-brand" />
            <div className="eyebrow eyebrow-brand">Cover CTA Analytics</div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              ["7d", cov.last_7_days],
              ["30d", cov.last_30_days],
              ["All time", cov.all_time],
            ].map(([label, group]) => (
              <div key={label} className="card-inner p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="eyebrow">{label}</div>
                  <div className="flex items-center gap-1 text-gold-400 text-xs font-manrope font-semibold">
                    <TrendingUp className="w-3 h-3" /> {group.total}
                  </div>
                </div>
                <div className="space-y-1.5 text-xs font-manrope">
                  <Row k="Register Company" v={group.register_company} testId={`cov-${label.toLowerCase()}-register-company`} />
                  <Row k="Join Professional" v={group.register_staff} testId={`cov-${label.toLowerCase()}-register-staff`} />
                  <Row k="Sign in (btn)" v={group.signin} />
                  <Row k="Sign in (top)" v={group.topbar_signin} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="txt-lo">{k}</span>
      <span className="font-outfit font-bold txt-hi" data-testid={testId}>{v}</span>
    </div>
  );
}
